// Import react elements
import { useCallback, useContext, useState, useEffect, useMemo } from 'react';

// Import context function
import { AuthContext } from "global/context/AuthContext";

// Import strich api calls
import { getImages, updateImage, addNewImage, deleteImage } from "api/APIStrichCalls";

// Import hooks
import { useToast } from "global/hooks/useToast";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
import { toBase64 } from "global/utilities/ConvertImage";


// Custom hook to manage fetching, filtering, updating, and uploading images
const useImageData = (typeFilter, categoryFilter, subcategoryFilter, sortOption, searchQuery) => {


    // Get the token from AuthContext
    const { token } = useContext(AuthContext);

    // Soldiers. Soldiers, toast? Geddit?
    const { toastError } = useToast();

    // Set empty state for the images data
    const [imageData, setImageData] = useState([]);

    // Set a loading state while getting images
    const [isLoading, setIsLoading] = useState(false);

    // Set a loading state when uploading new images
    const [isLoadingWhileAddingImages, setIsLoadingWhileAddingImages] = useState(false);

    // Track progress for the current group of image uploads
    const [uploadProgress, setUploadProgress] = useState({
        total: 0,
        completed: 0,
        succeeded: 0,
        failed: 0
    });


    const [, setPage] = useState(0);



    // Initial and paginated fetch
    const fetchImages = useCallback(async () => {

        if (!token) return;

        // Set loading to true
        setIsLoading(true);

        try {

            // Set the response from the API call
            const response = await getImages(token);

            if (response.success) {

                // Set the new images as API results
                const newImages = Array.isArray(response?.results) ? response.results : [];

                // Replace the local image list with the latest bucket data.
                setImageData(newImages);

                // Let callers inspect the latest image list after refresh.
                return newImages;

            } else {

                // Toast error
                toastError({ title: "Error", message: "Could not fetch images: ", apiErrorMessage: response.error_message, autoCloseMs: 6000 });

            }

        } finally {

            // Finished so set loading to false
            setIsLoading(false);

        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);



    // Run on initial mount
    useEffect(() => {

        if (!fetchImages) return;

        // Get the images
        fetchImages();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchImages]);



    // Load more for infinite scroll - not used yet
    const loadMoreImages = () => {

        setPage(prev => prev + 1);

    };



        // Upload new images
    const addImages = async (uploads) => {

        // Stop if there are no images to upload
        if (!Array.isArray(uploads) || uploads.length === 0) return [];

        // Set loading to true
        setIsLoadingWhileAddingImages(true);

        // Reset progress for this upload group
        setUploadProgress({
            total: uploads.length,
            completed: 0,
            succeeded: 0,
            failed: 0
        });

        // Build the classification values to apply to every uploaded image
        let uploadType = "";
        let uploadCategory = "";
        let uploadSubcategory = "";

        if (typeFilter !== "All") { uploadType = typeFilter; }
        if (categoryFilter !== "All") { uploadCategory = categoryFilter; }
        if (subcategoryFilter !== "All") { uploadSubcategory = subcategoryFilter; }

        try {

            // Convert and upload all images in parallel
            const uploadResults = await Promise.all(

                uploads.map(async ({ name, file }) => {

                    let response = null;

                    try {

                        // Convert the file to base64
                        const image_base64 = await toBase64(file);

                        // Build the image data for the API
                        const image = {
                            image_base64,
                            name,
                            type: uploadType,
                            category: uploadCategory,
                            subcategory: uploadSubcategory
                        };

                        // Upload the image
                        response = await addNewImage(token, image);

                    } catch (error) {

                        console.error("Could not upload image:", name, error);

                        // Return a normal failed response so one bad file does not stop the remaining uploads
                        response = {
                            success: false,
                            error_message: "Could not upload image"
                        };

                    }

                    // Update the live upload progress
                    setUploadProgress((previousProgress) => {

                        const updatedProgress = {
                            ...previousProgress,
                            completed: previousProgress.completed + 1
                        };

                        if (response?.success) {
                            updatedProgress.succeeded = previousProgress.succeeded + 1;
                        } else {
                            updatedProgress.failed = previousProgress.failed + 1;
                        }

                        return updatedProgress;

                    });

                    // Keep the filename with its API response
                    return {
                        name,
                        response
                    };

                })

            );

            // Extract the API responses
            const responses = uploadResults.map((uploadResult) => uploadResult.response);

            // Determine whether at least one image was uploaded
            const anySuccess = responses.some((response) => response?.success);

            // Get the filenames of failed uploads
            const failedUploads = uploadResults
                .filter((uploadResult) => !uploadResult.response?.success)
                .map((uploadResult) => uploadResult.name);

            // Alert the user when one or more files failed
            if (failedUploads.length > 0) {

                const message = "One or more of the images could NOT be uploaded:<br/><br/>" + failedUploads.join("<br/>");

                DisplayConfirmationDialog(

                    message,
                    "Ok",
                    "",

                    async function (is_confirmed) {

                        // Nothing further is required after acknowledging the failed uploads
                        if (!is_confirmed) return;

                    }

                );

            }

            // Get the IDs returned for successful uploads
            const uploadedImageIds = responses
                .filter((response) => response?.success)
                .map((response) => response?.results?.id)
                .filter((imageId) => imageId !== undefined && imageId !== null);

            // Start with no hydrated uploaded records
            let uploadedBucketImages = [];

            // Refresh the bucket when at least one upload succeeded
            if (anySuccess) {

                const refreshedImages = await fetchImages();

                // Find the newly uploaded records in the refreshed bucket
                if (Array.isArray(refreshedImages)) {

                    uploadedBucketImages = refreshedImages.filter((image) => {

                        return uploadedImageIds.some((uploadedImageId) => String(uploadedImageId) === String(image?.id));

                    });

                }

            }

            // Return the newly uploaded Image Bucket records
            return uploadedBucketImages;

        } finally {

            // Upload processing has finished
            setIsLoadingWhileAddingImages(false);

        }

    };



    // Update image data
    const updateSelectedImage = async (id, updatedImageData) => {

        //console.log("updates: ", updatedImageData);

        // Make the api call and always use the id supplied to this update helper
        const response = await updateImage(token, { ...updatedImageData, id });

        // Only update local data when the API update succeeded
        if (response?.success) {

            // Set the imageData so it updates immediately
            setImageData(prev =>

                // Map through to find the image with matching id and update with the updated image data
                prev.map(img => {

                    // Merge the updated fields into the matching image
                    if (String(img.id) === String(id)) return { ...img, ...updatedImageData };

                    // Leave every other image unchanged
                    return img;

                })

            );

        }

        // Return the response along with the image id
        return { id, ...response };

    };



    // Delete image(s)
    const deleteImages = async (imageIDs) => {

        // Need to edit this to actually delete the images - currently just marks it as deleted

        // Set up the payload for the call - map through each image id and add 'deleted' value  
        const deleteImagePayloads = imageIDs.map(id => ({ id, deleted: 'Y' }));


        // Delete all images and collect responses
        const responses = await Promise.all(

            // Map through the deleteImagePayloads and run the api call to delete the image
            //deleteImagePayloads.map(image => updateImage(token, image))
            deleteImagePayloads.map(image => deleteImage(token, image.id))

        );


        // Determine if any succeeded
        const anySuccess = responses.some(res => res.success);

        // For each response
        responses.forEach((response, index) => {

            // Check if it was successful
            if (response.success) {

                // Get the ID belonging to this successful delete response
                const deletedImageId = imageIDs[index];

                // Remove only the successfully deleted image from the current set
                setImageData(prev => prev.filter(img => img.id !== deletedImageId));

            }

        });


        // Collect an array of failed deletion ids and error messages
        const failedToDelete = deleteImagePayloads.map((image, index) => {

            // Get the response for the current image deletion attempt
            const response = responses[index];

            // If the deletion failed
            return !response?.success

                // Return an object with the image ID and error message
                ? { id: image.id, error_message: response?.error_message || 'Unknown error' }

                // Otherwise, return null (indicating success, nothing to track)
                : null;

            })

            // Remove nulls (i.e. successful deletions)
            .filter(Boolean); 


        // Check if there are any failed deletes so we can alert the user
        if (failedToDelete.length > 0) {

            // Set the dialog message based on number of fails
            const message =
                `${failedToDelete.length > 1 ? 'Some images' : 'Image'} could NOT be deleted:<br/><br/>` +

                // new Set([...]) removes duplicate error mesages
                [...new Set(failedToDelete.map(failedDelete => failedDelete.error_message))].join('<br/>');

            // Display dialog box to alert user that one or more of the images have failed to delete
            DisplayConfirmationDialog(

                message,
                "Ok",
                "",

                async function (is_confirmed) {

                    // If user confirms
                    if (is_confirmed) {
                         
                    }

                }

            );

        }

        // If any are successful deletions
        if (anySuccess) {

            // Refetch image data
            await fetchImages();

        }

    // Return the ids of images that failed to delete
    return failedToDelete;

    };



    // Sort logic
    const sortedImages = useMemo(() => {

        // Set the sorted image data
        const sorted = [...imageData];

        // Sort newest based on id
        if (sortOption === 'newest') {

            sorted.sort((a, b) => b.id - a.id);

            // Sort oldest based on id 
        } else if (sortOption === 'oldest') {

            sorted.sort((a, b) => a.id - b.id);

            // Sort image name alphebetically asc
        } else if (sortOption === 'filename-asc') {

            sorted.sort((a, b) => a.name.localeCompare(b.name));

            // Sort image name alphebetically desc
        } else if (sortOption === 'filename-desc') {

            sorted.sort((a, b) => b.name.localeCompare(a.name));

        }

        // Return the sorted data
        return sorted;

        // Run whenever imageData or sort option is changed
    }, [imageData, sortOption]);



    // Apply filters
    const filteredImages = useMemo(() => {

        // Filter the sorted image data into type, category and subcategory
        return sortedImages.filter(img => {

            // Filter by type
            const matchesType = typeFilter === 'All' || img.type === typeFilter;

            // Filter by category
            const matchesCat = categoryFilter === 'All' || img.category === categoryFilter;

            // Filter by subcategory
            const matchesSub = subcategoryFilter === 'All' || img.subcategory === subcategoryFilter;

            // Filter by search bar value
            const matchesSearch = !searchQuery || img.name.toLowerCase().includes(searchQuery.toLowerCase());

            // Return the matches
            return matchesType && matchesCat && matchesSub && matchesSearch;

        });

        // Re run whenever the sorted images, filters, or search bar value changes 
    }, [sortedImages, typeFilter, categoryFilter, subcategoryFilter, searchQuery]);



    // Memoize derived filter options based on current image data and selected filters
    const filters = useMemo(() => {

        // Get all unique types from imageData
        const types = [...new Set(imageData.map(i => i.type))];

        // If a specific type is selected, extract categories related to that type
        const categories = typeFilter && typeFilter !== 'All'
            ? [...new Set(imageData.filter(i => i.type === typeFilter).map(i => i.category))]
            : [];

        // If a specific category is selected, extract subcategories related to that category
        const subcategories = categoryFilter && categoryFilter !== 'All'
            ? [...new Set(imageData.filter(i => i.category === categoryFilter).map(i => i.subcategory))]
            : [];

        // Return the derived options for use in filter UI
        return { types, categories, subcategories };

        // Re run whenever imageData, type or category change
    }, [imageData, typeFilter, categoryFilter]);



    return {
        imageData,
        filteredImages,
        loadMoreImages,
        isLoading,
        updateSelectedImage,
        deleteImages,
        addImages,
        refreshImages: fetchImages,
        filters,
        setImageData,
        isLoadingWhileAddingImages,
        uploadProgress
    };

};

export default useImageData;
